import { BANK_TRANSACTION_CATEGORY_CODES } from '../banking/categories';

export const BANK_DRAFT_REVIEW_STATES = ['unreviewed', 'waiting_for_evidence', 'reviewed_without_invoice'];

// Keep statement-level selection limits separate from individual review states.
export function bankDraftSelectionIssues(choices = []) {
  const rows = {}, owners = new Map();
  let count = 0;
  choices.forEach(choice => {
    (choice.targets || []).forEach(target => {
      count += 1;
      const previous = owners.get(target.id);
      if (previous !== undefined) {
        const message = previous === choice.row.position
          ? `Transaction ${previous}: select this document only once.`
          : `Transactions ${previous} and ${choice.row.position} use the same document. Remove it from one transaction or choose a different document.`;
        rows[previous] = message;
        rows[choice.row.position] = message;
      } else owners.set(target.id, choice.row.position);
    });
  });
  return { rows, message: Object.values(rows)[0] || (choices.length > 100 || count > 100
    ? 'This confirmation supports up to 100 transaction decisions and 100 document selections. Reduce this selection before confirming.' : '') };
}
export function normalizeBankDraftReview(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).length !== 3 || !['state', 'reason', 'categoryCode'].every(key => Object.hasOwn(value, key))
    || !BANK_DRAFT_REVIEW_STATES.includes(value.state) || typeof value.reason !== 'string' || [...value.reason.trim()].length > 2000
    || (value.categoryCode !== null && !BANK_TRANSACTION_CATEGORY_CODES.includes(value.categoryCode))) {
    throw new Error('Choose a supported category and review decision; comments are optional and limited to 2000 characters.');
  }
  return { state: value.state, reason: value.reason.trim(), categoryCode: value.categoryCode };
}

export function validateBankDraftChoice(choice) {
  if (!choice?.row || !Number.isInteger(choice.row.position) || !Array.isArray(choice.targets)
    || !['invoice', 'purchase', 'evidence', 'review'].includes(choice.kind)) throw new Error('The saved transaction decision is invalid.');
  const review = choice.review === undefined ? null : normalizeBankDraftReview(choice.review);
  if (choice.kind === 'review' && (!review || choice.targets.length || choice.fee !== '0.0000')) throw new Error('A review-only decision cannot allocate a payment.');
  if (choice.kind !== 'review' && (!choice.targets.length || review?.state === 'reviewed_without_invoice')) throw new Error('Remove the draft match or evidence link before approving without an invoice.');
  return review;
}

export function changeBankDraftReview(choice, row, patch) {
  const previous = choice?.review || { state: row.existingTransaction?.bankReview?.state || 'unreviewed', reason: row.existingTransaction?.bankReview?.reason || '', categoryCode: null };
  const review = { ...previous, ...patch };
  const next = { ...(choice || { row, kind: 'review', targets: [], fee: '0.0000' }), review, suggested: false };
  validateBankDraftChoice(next);
  // Keep the actual input while typing; normalization happens at confirmation.
  return next;
}

// Discovery may show a different currency, but selecting it still uses the
// stricter target guard below. Never broaden business or acceptance boundaries.
export function bankDraftEvidenceCandidate(item, transaction, account = false) {
  const organizationId = item.organizationId || item.workspaceId;
  const status = String(item.status || '').toLowerCase();
  const type = String(item.documentType || '').toLowerCase().replaceAll(' ', '_');
  if (organizationId !== transaction.organizationId || item.businessId !== transaction.businessId || status !== 'accepted'
    || !['receipt', 'purchase_invoice'].includes(type) || item.archived || item.archivedAt || item.error
    || (account && (item.sourceStatus !== 'accepted' || !Number.isInteger(item.revision)))) return null;
  return account ? { ...item, workspaceId: organizationId, sourceId: item.sourceDocumentId, status: 'Accepted',
    documentType: type === 'receipt' ? 'Receipt' : 'Purchase invoice', archived: false } : item;
}

export function bankDraftEvidenceTarget(item, transaction, account = false) {
  const candidate = bankDraftEvidenceCandidate(item, transaction, account);
  return candidate && (!candidate.accountingEvidence?.currency || candidate.accountingEvidence.currency === transaction.currency) ? candidate : null;
}
